/**
 * Formatage des montants monétaires, source unique du monorepo.
 *
 * Douze implémentations s'en chargeaient auparavant, chacune avec sa propre
 * idée du symbole, de sa position et du nombre de décimales. Celle de catalog
 * portait la seule règle métier écrite : **en anglais le symbole précède le
 * montant** (`€35`, `$50`), dans toutes les autres langues il le suit avec une
 * espace (`35 €`, `50 $`). C'est elle qui est promue ici.
 *
 * Le module ne dépend de rien : il est consommable par les apps MUI comme par
 * celles en Tailwind (business-intelligence, pricing-simulator), ce qui est la
 * raison de ne pas l'avoir mis dans `@odys/design-system` — MUI-only par
 * l'arbitrage P0.5 de l'issue #2503.
 */

/**
 * Symboles connus, volontairement courts.
 *
 * `MXN` en est ABSENT à dessein : le peso mexicain partage le glyphe `$` avec
 * le dollar, donc `10 $` serait ambigu sur un écran qui affiche les deux. Une
 * devise sans symbole propre est rendue par son code — `10 MXN` — ce qu'un test
 * de catalog verrouillait déjà, et à juste titre. N'ajouter ici qu'un glyphe
 * non ambigu.
 */
const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
};

/** Devise de repli quand la source n'en porte pas. */
const FALLBACK_CURRENCY = "EUR";

/** Ce qu'on affiche à la place d'un montant absent. */
export const NO_AMOUNT = "—";

export type FormatPriceOptions = {
  /** Locale BCP 47. Pilote les séparateurs ET la position du symbole. */
  locale?: string;
  /** Code ISO 4217. Inconnu, il est affiché tel quel à la place du symbole. */
  currency?: string | null;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Texte rendu quand le montant est absent. Défaut : `—`. */
  fallback?: string;
};

/** Symbole d'une devise, ou son code quand il n'est pas connu. */
export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOL[currency] ?? currency;
}

/** `true` quand la devise a un glyphe propre, et pas seulement son code. */
function hasSymbol(currency: string): boolean {
  return currency in CURRENCY_SYMBOL;
}

/**
 * Symbole et position pour une devise dans une locale.
 *
 * Exposé séparément parce que plusieurs écrans composent eux-mêmes le montant —
 * un champ de saisie avec ornement, une légende de grille — et ont besoin des
 * deux morceaux plutôt que de la chaîne finale.
 */
export function currencyAdornment(
  currency: string,
  locale: string,
): { symbol: string; position: "start" | "end" } {
  // Un code n'est pas un glyphe : `CHF1234` et `MXN1234` se lisent comme un
  // seul jeton, là où `€1234` se lit comme un montant. La règle « en anglais
  // le symbole précède » ne s'applique donc qu'aux devises qui ont un
  // symbole ; les autres sont toujours suffixées.
  const prefixed = hasSymbol(currency) && locale.startsWith("en");
  return {
    symbol: currencySymbol(currency),
    position: prefixed ? "start" : "end",
  };
}

/** Arrondi à l'entier supérieur. `null` reste `null` — pas de prix, pas d'arrondi. */
export function ceilAmount(amount: number | null): number | null {
  return amount === null ? null : Math.ceil(amount);
}

/**
 * Montant réellement affichable : `null` quand la source n'a pas de prix, mais
 * AUSSI quand elle encode l'inconnu par `0` ou un négatif aberrant. Mieux vaut
 * ne rien afficher que « À partir de 0 € », qui se lit comme un prix gratuit
 * (cf. #2095).
 */
export function displayableAmount(amount: number | null): number | null {
  const ceiled = ceilAmount(amount);
  return ceiled === null || ceiled <= 0 ? null : ceiled;
}

export function formatPrice(
  amount: number | null | undefined,
  {
    locale = "en",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    fallback = NO_AMOUNT,
  }: FormatPriceOptions = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return fallback;
  }
  const { symbol, position } = currencyAdornment(
    currency ?? FALLBACK_CURRENCY,
    locale,
  );
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
  return position === "start" ? `${symbol}${number}` : `${number} ${symbol}`;
}
